/**
 * Queue filter state ↔ URL (IMPLEMENTATION_PLAN §12). The URL is the source of
 * truth: `/queue?queue=mine&priority=P1,P2&product=cgt&q=label`. Parsing drops
 * unknown values so a hand-edited URL can't send garbage to the API.
 */

export const QUEUES = ['all', 'mine', 'sla_risk', 'gxp', 'escalated'] as const
export type QueueKey = (typeof QUEUES)[number]

export const FILTER_PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const
export const FILTER_TYPES = ['defect', 'integration', 'data_correction', 'service_request', 'question'] as const

/** Chip filters that live in the URL as comma lists. */
export type ListFilterKey = 'priority' | 'product' | 'type' | 'customer'
export const LIST_FILTER_KEYS: readonly ListFilterKey[] = ['priority', 'product', 'type', 'customer']

export interface QueueFilters {
  queue: QueueKey
  priority: string[]
  /** Product keys (`cgt`, `eqms`, …). */
  product: string[]
  type: string[]
  /** Customer slugs. */
  customer: string[]
  /** One tenant (branch) id; only meaningful while exactly one customer is selected. */
  tenant: string
  q: string
}

export const EMPTY_FILTERS: QueueFilters = {
  queue: 'all',
  priority: [],
  product: [],
  type: [],
  customer: [],
  tenant: '',
  q: '',
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** Free-form keys (product, customer): lower-case slugs only. */
const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/

function splitList(raw: string | null): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values))
}

function parseList(key: ListFilterKey, raw: string | null): string[] {
  const values = splitList(raw)
  switch (key) {
    case 'priority':
      return unique(values.map((v) => v.toUpperCase()).filter((v) => (FILTER_PRIORITIES as readonly string[]).includes(v))).sort()
    case 'type':
      return unique(values.map((v) => v.toLowerCase()).filter((v) => (FILTER_TYPES as readonly string[]).includes(v)))
    default:
      return unique(values.map((v) => v.toLowerCase()).filter((v) => SLUG.test(v)))
  }
}

export function parseQueueFilters(search: URLSearchParams | string): QueueFilters {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const queueRaw = params.get('queue') ?? 'all'
  const queue = (QUEUES as readonly string[]).includes(queueRaw) ? (queueRaw as QueueKey) : 'all'
  const customer = parseList('customer', params.get('customer'))
  const tenantRaw = (params.get('tenant') ?? '').trim().toLowerCase()
  return {
    queue,
    priority: parseList('priority', params.get('priority')),
    product: parseList('product', params.get('product')),
    type: parseList('type', params.get('type')),
    customer,
    // A tenant belongs to one client: it only applies while exactly one customer is selected.
    tenant: customer.length === 1 && UUID.test(tenantRaw) ? tenantRaw : '',
    q: (params.get('q') ?? '').trim(),
  }
}

/** The URL form: defaults are omitted so `/queue` stays clean. */
export function toSearchParams(filters: QueueFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.queue !== 'all') params.set('queue', filters.queue)
  for (const key of LIST_FILTER_KEYS) {
    if (filters[key].length > 0) params.set(key, filters[key].join(','))
  }
  if (filters.tenant && filters.customer.length === 1) params.set('tenant', filters.tenant)
  if (filters.q.trim()) params.set('q', filters.q.trim())
  return params
}

/** The `GET /tickets` query: URL filters plus sort, page size and cursor. */
export function toApiParams(
  filters: QueueFilters,
  opts: { limit?: number; cursor?: string | null; sort?: string } = {},
): URLSearchParams {
  const params = toSearchParams(filters)
  params.set('queue', filters.queue)
  params.set('sort', opts.sort ?? 'sla')
  if (opts.limit) params.set('limit', String(opts.limit))
  if (opts.cursor) params.set('cursor', opts.cursor)
  return params
}

export function setListFilter(filters: QueueFilters, key: ListFilterKey, values: string[]): QueueFilters {
  const next = parseQueueFilters(toSearchParams({ ...filters, [key]: values }))
  // Changing the customer selection drops a tenant picked for the previous one.
  if (key === 'customer' && next.customer.join(',') !== filters.customer.join(',')) return { ...next, tenant: '' }
  return next
}

/** Tenant chip (single select): the last picked value wins; picking the current one clears it. */
export function setTenantFilter(filters: QueueFilters, values: string[]): QueueFilters {
  const fresh = values.filter((v) => v !== filters.tenant)
  const picked = fresh[fresh.length - 1] ?? ''
  return parseQueueFilters(toSearchParams({ ...filters, tenant: picked }))
}

export function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
}

/** "All" chip: clears every chip filter and the search, keeps the queue. */
export function clearChipFilters(filters: QueueFilters): QueueFilters {
  return { ...EMPTY_FILTERS, queue: filters.queue }
}

export function hasChipFilters(filters: QueueFilters): boolean {
  return LIST_FILTER_KEYS.some((k) => filters[k].length > 0) || filters.tenant.length > 0 || filters.q.length > 0
}

/**
 * Chip value text. Priorities that form a contiguous run collapse to a range
 * (`P1–P2`); anything else is listed, and more than two values summarize as
 * `first +n`. An empty list shows `emptyLabel` (`any` / `all`).
 */
export function chipValueText(
  key: ListFilterKey,
  values: string[],
  emptyLabel: string,
  labelOf: (value: string) => string = (v) => v,
): string {
  if (values.length === 0) return emptyLabel
  if (key === 'priority') {
    const nums = values.map((v) => Number(v.slice(1))).sort((a, b) => a - b)
    const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1)
    if (nums.length > 1 && contiguous) return `P${nums[0]}–P${nums[nums.length - 1]}`
    return nums.map((n) => `P${n}`).join(', ')
  }
  const labels = values.map(labelOf)
  if (labels.length <= 2) return labels.join(', ')
  return `${labels[0]} +${labels.length - 1}`
}
